import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { API } from "@/App";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import AdminE2EViewer from "@/components/admin/AdminE2EViewer";
import LocationInput from "@/components/LocationInput";
import {
  Search,
  Users,
  User,
  Mail,
  Phone,
  Building2,
  MapPin,
  Instagram,
  Globe,
  Clock,
  CheckCircle2,
  FileSignature,
  ClipboardList,
  ChevronRight,
  ExternalLink,
  Edit2,
  Trash2,
  Copy,
  Download,
  Calendar,
  Eye,
  UserPlus,
  X,
  Save,
  RefreshCw,
  Map,
  ArrowRight,
  Briefcase,
  Star,
  CreditCard,
  MessageSquare,
  Palette,
  Heart,
  Scissors,
  ShoppingBag,
  BookOpen,
  Home,
  Car,
  Sparkles,
  Link,
  Settings,
  ChefHat,
  GraduationCap,
  Paintbrush,
  Camera,
  Upload
} from "lucide-react";

// ==================== HUMAN-FRIENDLY LABELS ====================
const FIELD_LABELS = {
  business_name: "Business Name", first_name: "First Name", last_name: "Last Name",
  email: "Email", phone: "Phone", location: "Location", city: "City",
  instagram: "Instagram", tiktok: "TikTok", website: "Website", facebook: "Facebook",
  google: "Google Business", yelp: "Yelp", styleseat: "StyleSeat", booksy: "Booksy",
  discovery_channels: "Where Clients Find You", primary_discovery: "Most Clients Come From",
  primary_discovery_channels: "Primary Discovery Channels",
  booking_method: "How Clients Book", booking_platform: "Booking Platform",
  booking_platforms: "Booking Platforms Used",
  typical_response_time: "Typical Response Time",
  service_location: "Where Services Are Provided", appointment_prep: "Client Prep Instructions",
  what_to_bring: "What Clients Should Bring",
  follow_up_method: "Follow-Up Method", rebooking_approach: "Rebooking Approach",
  client_retention_notes: "Client Retention Strategy", common_questions: "Common Questions Asked",
  info_wish_clients_knew: "What Clients Should Know",
  inspo_collection_method: "How Clients Share Inspiration", inspo_platforms: "Inspiration Platforms",
  uses_mood_boards: "Uses Mood Boards",
  brands_used: "Brands and Products Used", sell_products: "Sells Products",
  products_sold: "Products Sold", product_recommendations: "Product Recommendations",
  provider_prep_routine: "Prep Routine Before Appointments",
  review_client_history: "Reviews Client History",
  cancellation_policy: "Cancellation Policy",
  offers_packages: "Offers Packages or Memberships", packages_details: "Package Details",
  payment_methods: "Accepted Payment Methods",
  show_prices: "Show Prices to Clients", require_deposit: "Requires Deposit",
  send_reminders: "Sends Reminders", collect_preferences: "Collects Client Preferences",
  client_defaults: "Info Collected About Every Client",
  has_logo: "Has Logo", logo_url: "Logo",
  anything_else: "Additional Notes", services_offered: "Services Offered",
  experience: "Years of Experience", years_experience: "Years of Experience",
  team_size: "Team Size", gender: "Gender",
};

const VALUE_LABELS = {
  booking_platform: "Uses a booking platform", dm_text: "DM or Text", phone_call: "Phone Call",
  walk_ins: "Walk-ins",
  within_hour: "Within an hour", same_day: "Same day", within_24_hours: "Within 24 hours", when_i_can: "When I can",
  home_studio: "Home or Studio", salon_spa: "Salon or Spa", mobile: "Mobile (Travel to client)",
  rented_space: "Rented Space", outdoor_events: "Outdoor or Events",
  wash_hair: "Wash hair before visit", clean_face: "Come with a clean face",
  bring_photos: "Bring reference photos", no_product: "No product on hair or skin", eat_beforehand: "Eat beforehand",
  text_checkin: "Text check-in after appointment", send_photos: "Send photos of the work",
  rebook_reminder: "Rebook reminder", birthday_messages: "Birthday messages",
  allergies: "Allergies", skin_type: "Skin Type", hair_color: "Hair Color and Texture",
  sensitivities: "Sensitivities", dietary: "Dietary Restrictions", scents: "Scent Preferences",
  medical: "Medical Conditions", previous_treatments: "Previous Treatments", style_prefs: "Style Preferences",
  pinterest_board: "Pinterest Board", screenshots: "Screenshots", show_phone: "Show phone in person",
  describe_verbally: "Describe verbally",
  venmo: "Venmo", cashapp: "CashApp", zelle: "Zelle", stripe: "Stripe", paypal: "PayPal",
  square: "Square", cash: "Cash", card_reader: "Card Reader", acuity: "Acuity",
};

function humanLabel(key) {
  return FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
function humanValue(val) {
  if (val === true) return "Yes";
  if (val === false) return "No";
  if (val === null || val === undefined || val === "") return null;
  if (Array.isArray(val)) return val.map(v => VALUE_LABELS[v] || String(v).replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())).join(", ");
  if (typeof val === "object") {
    const filled = Object.entries(val).filter(([, v]) => v && v !== "");
    if (filled.length === 0) return null;
    return filled.map(([k, v]) => `${humanLabel(k)}: ${v}`).join("\n");
  }
  return VALUE_LABELS[String(val)] || String(val);
}

function isUrl(str) { return typeof str === "string" && (str.startsWith("http") || str.startsWith("www.")); }
function isSocial(key) { return ["instagram", "tiktok", "facebook", "twitter", "linkedin", "youtube"].includes(key); }
function socialUrl(key, val) {
  const handle = String(val).replace(/^@/, "");
  const map = { instagram: `https://instagram.com/${handle}`, tiktok: `https://tiktok.com/@${handle}`, facebook: `https://facebook.com/${handle}`, twitter: `https://twitter.com/${handle}`, linkedin: `https://linkedin.com/in/${handle}`, youtube: `https://youtube.com/@${handle}` };
  return map[key] || val;
}

function groupIntakeData(responses) {
  if (!responses || Object.keys(responses).length === 0) return [];
  const sections = [
    { title: "Contact and Business", icon: Building2, keys: ["business_name", "first_name", "last_name", "email", "phone", "location", "city"] },
    { title: "Online Presence", icon: Globe, keys: ["instagram", "tiktok", "facebook", "website", "google", "yelp", "styleseat", "booksy", "discovery_channels", "primary_discovery", "primary_discovery_channels"] },
    { title: "Booking and Scheduling", icon: Calendar, keys: ["booking_method", "booking_platform", "booking_platforms", "typical_response_time"] },
    { title: "Services and Experience", icon: Sparkles, keys: ["services_offered", "experience", "years_experience", "team_size", "gender", "service_location", "appointment_prep", "what_to_bring"] },
    { title: "Client Relationships", icon: Heart, keys: ["follow_up_method", "rebooking_approach", "client_retention_notes", "common_questions", "info_wish_clients_knew", "inspo_collection_method", "inspo_platforms", "uses_mood_boards"] },
    { title: "Products and Brands", icon: ShoppingBag, keys: ["brands_used", "sell_products", "products_sold", "product_recommendations"] },
    { title: "Business Operations", icon: Briefcase, keys: ["provider_prep_routine", "review_client_history", "cancellation_policy", "offers_packages", "packages_details"] },
    { title: "Payment and Settings", icon: CreditCard, keys: ["payment_methods", "show_prices", "require_deposit", "send_reminders", "collect_preferences", "client_defaults"] },
    { title: "Additional Info", icon: MessageSquare, keys: ["has_logo", "logo_url", "anything_else"] },
  ];
  const result = [];
  const usedKeys = new Set();
  for (const section of sections) {
    const fields = [];
    for (const key of section.keys) {
      if (key in responses) {
        const val = humanValue(responses[key]);
        if (val !== null) { fields.push({ key, label: humanLabel(key), value: val, raw: responses[key] }); usedKeys.add(key); }
      }
    }
    if (fields.length > 0) result.push({ ...section, fields });
  }
  const remaining = [];
  for (const [key, val] of Object.entries(responses)) {
    if (!usedKeys.has(key) && !key.startsWith("_")) {
      const formatted = humanValue(val);
      if (formatted !== null) { remaining.push({ key, label: humanLabel(key), value: formatted, raw: val }); }
    }
  }
  if (remaining.length > 0) result.push({ title: "Other", icon: BookOpen, fields: remaining });
  return result;
}

// ==================== FIELD VALUE RENDERER ====================
function FieldValue({ field }) {
  const { key, value, raw } = field;
  // Logo image
  if (key === "logo_url" && typeof raw === "string" && raw.startsWith("http")) {
    return <img src={raw} alt="Logo" className="w-16 h-16 rounded-lg object-cover border" />;
  }
  // Social media link
  if (isSocial(key) && raw) {
    return (
      <a href={socialUrl(key, raw)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
        {String(raw)} <ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  // URL
  if (isUrl(String(raw))) {
    return (
      <a href={String(raw).startsWith("http") ? raw : `https://${raw}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
        {String(raw)} <ExternalLink className="w-3 h-3" />
      </a>
    );
  }
  // Multiline (discovery_channels object)
  if (value.includes("\n")) {
    return (
      <div className="space-y-1">
        {value.split("\n").map((line, i) => {
          const parts = line.split(": ");
          const linkKey = parts[0]?.toLowerCase().trim();
          const linkVal = parts.slice(1).join(": ").trim();
          if (isSocial(linkKey) && linkVal) {
            return (
              <div key={i} className="flex items-center gap-1">
                <span className="text-gray-500 text-xs w-20 shrink-0">{parts[0]}:</span>
                <a href={socialUrl(linkKey, linkVal)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                  {linkVal} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            );
          }
          if (isUrl(linkVal)) {
            return (
              <div key={i} className="flex items-center gap-1">
                <span className="text-gray-500 text-xs w-20 shrink-0">{parts[0]}:</span>
                <a href={linkVal.startsWith("http") ? linkVal : `https://${linkVal}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                  {linkVal} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            );
          }
          return <p key={i} className="font-medium text-sm">{line}</p>;
        })}
      </div>
    );
  }
  return <p className="font-medium text-sm">{value}</p>;
}


// ==================== PERSON DETAIL MODAL ====================
function PersonDetailModal({ person, isOpen, onClose, categoryColors, onRefresh, category }) {
  const [editing, setEditing] = useState(false);
  const [editingIntake, setEditingIntake] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [editIntakeData, setEditIntakeData] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const [intakeSubTab, setIntakeSubTab] = useState(0);
  const [ndaData, setNdaData] = useState(null);
  const [loadingNda, setLoadingNda] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [portfolioImages, setPortfolioImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Fetch portfolio images when tab changes
  const fetchPortfolio = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const inviteId = person?.invite_id || person?.portal_id;
      const res = await fetch(`${API}/admin/invite-portfolio/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPortfolioImages(data.portfolio_images || []);
      }
    } catch (err) {
      console.error("Error fetching portfolio:", err);
    }
  };

  // Handle portfolio image upload
  const handlePortfolioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/upload/image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        const newImages = [...portfolioImages, data.url];
        setPortfolioImages(newImages);
        
        // Save to backend
        const inviteId = person?.invite_id || person?.portal_id;
        await fetch(`${API}/admin/invite-portfolio/${inviteId}`, {
          method: "PUT",
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ portfolio_images: newImages })
        });
        
        toast.success("Image uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      toast.error("Error uploading image");
      console.error(err);
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle portfolio image remove
  const handleRemovePortfolioImage = async (index) => {
    const newImages = portfolioImages.filter((_, i) => i !== index);
    setPortfolioImages(newImages);
    
    try {
      const token = localStorage.getItem("session_token");
      const inviteId = person?.invite_id || person?.portal_id;
      await fetch(`${API}/admin/invite-portfolio/${inviteId}`, {
        method: "PUT",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ portfolio_images: newImages })
      });
      toast.success("Image removed");
    } catch (err) {
      toast.error("Error removing image");
    }
  };

  // Fetch portfolio when tab changes to portfolio
  useEffect(() => {
    if (activeTab === "portfolio" && person) {
      fetchPortfolio();
    }
  }, [activeTab, person]);

  // Fetch NDA data when tab changes to NDA view
  const fetchNdaData = async () => {
    if (!person?.nda_signed) return;
    setLoadingNda(true);
    try {
      const token = localStorage.getItem("session_token");
      const inviteId = person.invite_id || person.portal_id || person.member_id;
      const res = await fetch(`${API}/admin/portal-invite/${inviteId}/nda`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNdaData(data.nda);
      }
    } catch (err) {
      console.error("Failed to fetch NDA:", err);
    } finally {
      setLoadingNda(false);
    }
  };

  // Fetch edit history
  const fetchEditHistory = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const inviteId = person.invite_id || person.portal_id || person.member_id;
      const res = await fetch(`${API}/admin/intake-history/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEditHistory(data.history || []);
      }
    } catch (err) {
      console.error("Failed to fetch edit history:", err);
    }
  };

  if (!person) return null;

  const isPending = person.type === "invite";
  const intakeResponses = person.intake_responses || {};
  const prefill = person.prefill || {};
  const groupedData = groupIntakeData(intakeResponses);

  const journeyStages = [
    { id: "invited", label: "Invited", icon: Mail, completed: true, date: person.created_at },
    { id: "accepted", label: "Accepted", icon: CheckCircle2, completed: !isPending, date: person.accepted_at },
    { id: "nda", label: "NDA Signed", icon: FileSignature, completed: person.nda_signed, date: person.nda_signed_at },
    { id: "intake", label: "Intake Done", icon: ClipboardList, completed: person.intake_completed, date: person.intake_completed_at },
    { id: "active", label: "Active", icon: Users, completed: person.status === "active" },
  ];

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
  const formatDateTime = (d) => d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";

  const getStageUrl = (stageId) => {
    const base = window.location.origin;
    const token = person.magic_token;
    const p = person.portal_id || person.invite_id;
    
    switch (stageId) {
      case "invited": 
        return token ? `${base}/portal/invite/${token}` : null;
      case "accepted": 
        return token ? `${base}/portal/invite/${token}` : null;
      case "nda": 
        return p ? `${base}/pilot/nda/${p}` : (token ? `${base}/portal/invite/${token}` : null);
      case "intake": 
        return p ? `${base}/pilot/intake/${p}` : null;
      case "active": 
        return p ? `${base}/pilot/confirmation/${p}` : null;
      default: 
        return null;
    }
  };

  const handleStartEdit = () => {
    setEditData({
      name: person.user_name || person.name || "",
      email: person.user_email || person.email || "",
      phone: person.phone || "",
      company_name: person.company_name || "",
      location: intakeResponses.location || prefill.location || "",
      instagram: intakeResponses.instagram || prefill.instagram || "",
      website: intakeResponses.website || prefill.website || "",
      admin_notes: person.admin_notes || "",
    });
    setEditing(true);
  };

  const handleStartIntakeEdit = () => {
    setEditIntakeData({ ...intakeResponses });
    setEditingIntake(true);
    fetchEditHistory();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const endpoint = isPending ? `${API}/admin/portal-invites/${person.invite_id}` : `${API}/admin/portal-members/${person.member_id}`;
      const res = await fetch(endpoint, { method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(editData) });
      if (res.ok) { toast.success("Profile updated"); setEditing(false); onRefresh?.(); } else toast.error("Failed to update");
    } catch { toast.error("Error saving"); }
    finally { setSaving(false); }
  };

  const handleSaveIntake = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const inviteId = person.invite_id || person.portal_id || person.member_id;
      const res = await fetch(`${API}/admin/intake-responses/${inviteId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ intake_responses: editIntakeData })
      });
      if (res.ok) {
        toast.success("Intake responses updated");
        setEditingIntake(false);
        onRefresh?.();
        fetchEditHistory();
      } else {
        toast.error("Failed to update intake");
      }
    } catch {
      toast.error("Error saving intake");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadNDA = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const inviteId = person.invite_id || person.portal_id || person.member_id;
      const res = await fetch(`${API}/admin/nda/signed/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { toast.error("Failed to fetch NDA data"); return; }
      const data = await res.json();
      
      // Generate PDF
      const doc = new jsPDF();
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.text("PHYGITAL", 105, 20, { align: "center" });
      doc.setFontSize(14);
      doc.text("NON-DISCLOSURE AGREEMENT", 105, 30, { align: "center" });
      doc.setDrawColor(128, 0, 128);
      doc.setLineWidth(0.5);
      doc.line(20, 35, 190, 35);
      
      // Strip HTML from content
      const plainText = (data.nda_content || "")
        .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\n$1\n')
        .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
        .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\n\n+/g, '\n\n')
        .trim();
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const lines = doc.splitTextToSize(plainText, 170);
      let y = 45;
      const pageHeight = doc.internal.pageSize.height;
      
      for (let i = 0; i < lines.length; i++) {
        if (y > pageHeight - 50) { doc.addPage(); y = 20; }
        doc.text(lines[i], 20, y);
        y += 5;
      }
      
      // Signature section
      if (y > pageHeight - 80) { doc.addPage(); y = 30; }
      y += 15;
      doc.setDrawColor(128, 0, 128);
      doc.line(20, y, 190, y);
      y += 10;
      
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("ELECTRONIC SIGNATURE", 20, y);
      y += 10;
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("This document has been electronically signed and is legally binding.", 20, y);
      y += 15;
      
      doc.setFontSize(11);
      doc.text("Signed by:", 20, y);
      doc.setFont("times", "italic");
      doc.setFontSize(14);
      doc.text(data.signer_name || "Unknown", 50, y);
      y += 10;
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const signedDate = data.signed_at ? new Date(data.signed_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "Unknown";
      doc.text(`Date: ${signedDate}`, 20, y);
      y += 6;
      doc.text(`Email: ${data.signer_email || ""}`, 20, y);
      y += 6;
      doc.text(`Document ID: NDA-${inviteId}`, 20, y);
      
      y += 15;
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text("This electronically signed document is valid and enforceable under the E-SIGN Act.", 105, y, { align: "center" });
      doc.text("© 2026 Phygital Systems. All rights reserved.", 105, y + 5, { align: "center" });
      
      const fileName = `Phygital_NDA_Signed_${(data.signer_name || "").replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`;
      doc.save(fileName);
      toast.success("NDA PDF downloaded");
    } catch (err) {
      toast.error("Error downloading NDA");
      console.error(err);
    }
  };

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "intake", label: "Intake Responses", count: groupedData.length },
    { id: "pilots", label: "Pilots" },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="person-detail-modal">
        {/* Header - Fixed at top */}
        <div className="border-b px-6 pt-5 pb-4 shrink-0 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {person.user_picture ? (
                <img src={person.user_picture} alt="" className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <div className={`w-12 h-12 rounded-full ${categoryColors.bg} text-white flex items-center justify-center text-lg font-semibold`}>
                  {(person.user_name || person.name || "U").charAt(0).toUpperCase()}
                </div>
              )}
              <div>
                <h2 className="text-lg font-semibold">{person.user_name || person.name || "Unknown"}</h2>
                <p className="text-sm text-gray-500">{person.user_email || person.email}</p>
                {person.company_name && <p className="text-xs text-gray-400 flex items-center gap-1"><Building2 className="w-3 h-3" />{person.company_name}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {person.nda_signed && (
                <Button variant="outline" size="sm" onClick={handleDownloadNDA} title="Download Signed NDA" data-testid="download-nda-btn">
                  <Download className="w-4 h-4 mr-1" />NDA
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={async () => {
                try {
                  const intakeId = person.intake_id || person.id;
                  if (!intakeId) { toast.error("No intake ID found"); return; }
                  const token = localStorage.getItem("session_token");
                  const res = await fetch(`${API}/admin/sync-intake-to-people/${intakeId}`, {
                    method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
                  });
                  if (res.ok) {
                    const data = await res.json();
                    toast.success(`Synced! User: ${data.synced?.user ? "Yes" : "No"}, Org: ${data.synced?.org ? "Yes" : "No"}`);
                  } else { toast.error("Sync failed"); }
                } catch { toast.error("Sync failed"); }
              }} data-testid="sync-to-people-btn" title="Sync this person's info to Admin People DB (Users + Orgs)">
                <ArrowRight className="w-4 h-4 mr-1" />Sync to People
              </Button>
              {!editing ? <Button variant="outline" size="sm" onClick={handleStartEdit}><Edit2 className="w-4 h-4 mr-1" />Edit</Button> : (
                <><Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button><Button size="sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</Button></>
              )}
            </div>
          </div>
          {/* Tab Navigation */}
          <div className="flex gap-1 mt-4 -mb-4">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setIntakeSubTab(0); }}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? `${categoryColors.text} border-current` : "text-gray-500 border-transparent hover:text-gray-700"}`}
                data-testid={`person-tab-${tab.id}`}>
                {tab.label}{tab.count > 0 && <span className="ml-1 text-xs opacity-60">({tab.count})</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Content - Scrollable area */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {/* OVERVIEW */}
          {activeTab === "overview" && (
            <div className="space-y-5">
              {/* Journey */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Map className="w-4 h-4" />Journey Progress
                  <span className="text-xs text-gray-400 font-normal ml-auto">Click to view as participant</span>
                </h4>
                <div className="flex items-center gap-1 flex-wrap">
                  {journeyStages.map((stage, idx) => {
                    const Icon = stage.icon;
                    const url = getStageUrl(stage.id);
                    return (
                      <div key={stage.id} className="flex items-center">
                        <div className="flex flex-col items-center">
                          <button onClick={() => url ? window.open(url, "_blank") : toast.info(`No link for "${stage.label}" yet`)}
                            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110 ${stage.completed ? `${categoryColors.bg} text-white` : "bg-gray-100 text-gray-400 hover:bg-gray-200"}`} data-testid={`stage-btn-${stage.id}`}>
                            <Icon className="w-4 h-4" />
                          </button>
                          <span className={`text-[10px] mt-1 ${stage.completed ? categoryColors.text : "text-gray-400"}`}>{stage.label}</span>
                          {stage.date && stage.completed && <span className="text-[9px] text-gray-400">{formatDate(stage.date)}</span>}
                        </div>
                        {idx < journeyStages.length - 1 && <div className={`w-6 h-0.5 mx-0.5 ${stage.completed && journeyStages[idx + 1].completed ? categoryColors.bg : "bg-gray-200"}`} />}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Contact */}
              {editing ? (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">Edit Contact Info</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" }, { key: "company_name", label: "Company" }, { key: "location", label: "Location" }, { key: "instagram", label: "Instagram" }, { key: "website", label: "Website" }].map(f => (
                      <div key={f.key}><label className="text-xs text-gray-500">{f.label}</label><Input value={editData[f.key] || ""} onChange={e => setEditData(p => ({ ...p, [f.key]: e.target.value }))} /></div>
                    ))}
                  </div>
                  <div><label className="text-xs text-gray-500">Admin Notes</label><Textarea value={editData.admin_notes || ""} onChange={e => setEditData(p => ({ ...p, admin_notes: e.target.value }))} rows={2} /></div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {[{ icon: Mail, val: person.user_email || person.email, type: "email" }, { icon: Phone, val: person.phone || intakeResponses.phone, type: "phone" }, { icon: Building2, val: person.company_name || intakeResponses.business_name }, { icon: MapPin, val: intakeResponses.location || intakeResponses.city || prefill.city }].filter(f => f.val).map((f, i) => {
                      const Icon = f.icon;
                      const content = f.type === "email" ? (
                        <a href={`mailto:${f.val}`} className="text-blue-600 hover:underline">{f.val}</a>
                      ) : f.type === "phone" ? (
                        <a href={`tel:${f.val}`} className="text-blue-600 hover:underline">{f.val}</a>
                      ) : <span>{f.val}</span>;
                      return <div key={i} className="flex items-center gap-2 text-sm"><Icon className="w-4 h-4 text-gray-400 shrink-0" />{content}</div>;
                    })}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {(intakeResponses.instagram || prefill.instagram) && (
                      <a href={socialUrl("instagram", intakeResponses.instagram || prefill.instagram)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-pink-600 hover:underline">
                        <Instagram className="w-4 h-4" />{intakeResponses.instagram || prefill.instagram}<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {intakeResponses.tiktok && (
                      <a href={socialUrl("tiktok", intakeResponses.tiktok)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-gray-700 hover:underline">
                        TikTok: {intakeResponses.tiktok}<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {intakeResponses.facebook && (
                      <a href={socialUrl("facebook", intakeResponses.facebook)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-700 hover:underline">
                        Facebook: {intakeResponses.facebook}<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {(intakeResponses.website || prefill.website) && (
                      <a href={(intakeResponses.website || prefill.website).startsWith("http") ? (intakeResponses.website || prefill.website) : `https://${intakeResponses.website || prefill.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-blue-600 hover:underline">
                        <Globe className="w-4 h-4" />Website<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {intakeResponses.google && (
                      <a href={intakeResponses.google.startsWith("http") ? intakeResponses.google : `https://g.co/kgs/${intakeResponses.google}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-green-600 hover:underline">
                        Google Business<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {intakeResponses.yelp && (
                      <a href={intakeResponses.yelp.startsWith("http") ? intakeResponses.yelp : `https://yelp.com/biz/${intakeResponses.yelp}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-red-600 hover:underline">
                        Yelp<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {intakeResponses.styleseat && (
                      <a href={intakeResponses.styleseat.startsWith("http") ? intakeResponses.styleseat : `https://styleseat.com/${intakeResponses.styleseat}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-purple-600 hover:underline">
                        StyleSeat<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {intakeResponses.booksy && (
                      <a href={intakeResponses.booksy.startsWith("http") ? intakeResponses.booksy : `https://booksy.com/${intakeResponses.booksy}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-teal-600 hover:underline">
                        Booksy<ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  {Object.keys(intakeResponses).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Highlights</h4>
                      <div className="flex flex-wrap gap-2">
                        {intakeResponses.services_offered && <Badge variant="secondary" className="text-xs"><Scissors className="w-3 h-3 mr-1" />{humanValue(intakeResponses.services_offered)}</Badge>}
                        {(intakeResponses.experience || intakeResponses.years_experience) && <Badge variant="secondary" className="text-xs"><Star className="w-3 h-3 mr-1" />{intakeResponses.experience || intakeResponses.years_experience} years</Badge>}
                        {intakeResponses.team_size && <Badge variant="secondary" className="text-xs"><Users className="w-3 h-3 mr-1" />Team: {intakeResponses.team_size}</Badge>}
                        {intakeResponses.payment_methods && <Badge variant="secondary" className="text-xs"><CreditCard className="w-3 h-3 mr-1" />{humanValue(intakeResponses.payment_methods)}</Badge>}
                      </div>
                    </div>
                  )}
                  {Object.keys(prefill).length > 0 && !person.intake_completed && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Pre-filled by Admin <Badge variant="secondary" className="ml-1 text-xs">From Invite</Badge></h4>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                        {Object.entries(prefill).filter(([,v]) => v).map(([k, v]) => (
                          <div key={k} className="flex justify-between items-start text-sm">
                            <span className="text-amber-700">{humanLabel(k)}</span><span className="font-medium text-right max-w-[60%]">{humanValue(v)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {person.admin_notes && <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs font-medium text-gray-500 mb-1">Admin Notes</p><p className="text-sm">{person.admin_notes}</p></div>}
                  <div className="text-xs text-gray-400">{person.created_at && <span>Invited {formatDate(person.created_at)}</span>}{person.updated_at && <span className="ml-3">Updated {formatDate(person.updated_at)}</span>}</div>
                </div>
              )}
            </div>
          )}

          {/* INTAKE RESPONSES - With sub-tabs for sections */}
          {activeTab === "intake" && (
            <div className="space-y-4">
              {/* Edit/View Toggle */}
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">Intake Responses</h3>
                {groupedData.length > 0 && (
                  editingIntake ? (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingIntake(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleSaveIntake} disabled={saving} className={categoryColors.bg}>
                        {saving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        Save Changes
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={handleStartIntakeEdit}>
                      <Edit2 className="w-4 h-4 mr-1" /> Edit Responses
                    </Button>
                  )
                )}
              </div>
              
              {groupedData.length === 0 ? (
                <div className="text-center py-12 text-gray-500"><ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" /><p>No intake responses yet</p><p className="text-sm mt-1">Responses appear here once the participant completes their intake form</p></div>
              ) : (
                <>
                  {/* Section sub-tabs */}
                  <div className="flex flex-wrap gap-1 border-b pb-2">
                    {groupedData.map((section, idx) => {
                      const SIcon = section.icon;
                      return (
                        <button key={section.title} onClick={() => setIntakeSubTab(idx)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${intakeSubTab === idx ? `${categoryColors.bg} text-white` : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                          data-testid={`intake-section-${idx}`}>
                          <SIcon className="w-3 h-3" />{section.title}
                        </button>
                      );
                    })}
                  </div>
                  {/* Active section content - View or Edit mode */}
                  {groupedData[intakeSubTab] && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        {(() => { const SIcon = groupedData[intakeSubTab].icon; return <SIcon className="w-4 h-4" />; })()}
                        {groupedData[intakeSubTab].title}
                        <span className="text-xs font-normal text-gray-400 ml-auto">{groupedData[intakeSubTab].fields.length} fields</span>
                      </h4>
                      <div className="space-y-3">
                        {groupedData[intakeSubTab].fields.map(field => (
                          <div key={field.key} className="bg-white rounded-md p-3 border">
                            <p className="text-gray-500 text-xs mb-1">{field.label}</p>
                            {editingIntake ? (
                              <Input
                                value={editIntakeData[field.key] || ""}
                                onChange={(e) => setEditIntakeData(prev => ({ ...prev, [field.key]: e.target.value }))}
                                placeholder={`Enter ${field.label.toLowerCase()}`}
                              />
                            ) : (
                              <FieldValue field={field} />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Edit History */}
                  {editHistory.length > 0 && (
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Edit History
                      </h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {editHistory.map((entry, idx) => (
                          <div key={idx} className="text-xs bg-gray-50 rounded p-2 flex justify-between items-start">
                            <div>
                              <span className="font-medium">{entry.edited_by_name || entry.edited_by}</span>
                              <span className="text-gray-500"> edited </span>
                              <span className="text-purple-600">{entry.fields_changed?.join(", ") || "responses"}</span>
                            </div>
                            <span className="text-gray-400 whitespace-nowrap ml-2">{formatDateTime(entry.edited_at)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Navigation */}
                  <div className="flex items-center justify-between pt-2">
                    <Button variant="outline" size="sm" onClick={() => setIntakeSubTab(Math.max(0, intakeSubTab - 1))} disabled={intakeSubTab === 0}>
                      <ArrowRight className="w-4 h-4 mr-1 rotate-180" />Previous
                    </Button>
                    <span className="text-xs text-gray-400">{intakeSubTab + 1} of {groupedData.length}</span>
                    <Button variant="outline" size="sm" onClick={() => setIntakeSubTab(Math.min(groupedData.length - 1, intakeSubTab + 1))} disabled={intakeSubTab === groupedData.length - 1}>
                      Next<ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* PORTFOLIO */}
          {activeTab === "portfolio" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Portfolio Images
                </h4>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePortfolioUpload}
                    disabled={uploadingImage}
                  />
                  <Button variant="outline" size="sm" disabled={uploadingImage} asChild>
                    <span>
                      {uploadingImage ? "Uploading..." : (
                        <>
                          <Upload className="w-4 h-4 mr-1" />
                          Add Image
                        </>
                      )}
                    </span>
                  </Button>
                </label>
              </div>
              
              <p className="text-xs text-gray-500">
                Upload images to replace color block placeholders on this person's invite page demo content.
              </p>
              
              {portfolioImages.length === 0 ? (
                <div className="border-2 border-dashed rounded-lg p-8 text-center text-gray-400">
                  <Camera className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No portfolio images yet</p>
                  <p className="text-xs mt-1">Upload images to customize their invite page</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {portfolioImages.map((img, idx) => (
                    <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100">
                      <img src={img} alt={`Portfolio ${idx + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => handleRemovePortfolioImage(idx)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs"
                        title="Remove image"
                      >
                        ×
                      </button>
                      <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                        #{idx + 1}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="text-xs text-gray-400 mt-4">
                <p><strong>Image order:</strong></p>
                <ul className="list-disc ml-4 mt-1 space-y-0.5">
                  <li>Images 1-4: Portfolio showcase section</li>
                  <li>Images 5-10: Look options grid</li>
                </ul>
              </div>
            </div>
          )}

          {/* PILOTS */}
          {activeTab === "pilots" && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Pilot Memberships</h4>
              <div className={`flex items-center justify-between p-3 rounded-lg border ${categoryColors.light} ${categoryColors.border}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full ${categoryColors.bg} text-white flex items-center justify-center text-sm font-medium`}>{(category || "P").charAt(0).toUpperCase()}</div>
                  <div><p className="font-medium text-sm capitalize">{category} Pilot</p><p className="text-xs text-gray-500">{person.intake_completed ? "Intake complete" : isPending ? "Pending invite" : "In progress"}</p></div>
                </div>
                <Badge className={person.intake_completed ? "bg-green-500" : isPending ? "bg-amber-500" : "bg-blue-500"}>{person.intake_completed ? "Active" : isPending ? "Pending" : "In Progress"}</Badge>
              </div>
              <p className="text-xs text-gray-400 mt-3">Free legacy access for life (excluding enterprise/expansion tiers)</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ==================== MAIN PEOPLE TAB ====================
export default function PeopleTab({ subCategoryData, categoryColors, category, subCategoryId, onRefresh }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showAddInvite, setShowAddInvite] = useState(false);
  const [showEditInvite, setShowEditInvite] = useState(false);
  const [editingInvite, setEditingInvite] = useState(null);
  const [showAllIntakes, setShowAllIntakes] = useState(false);
  const [showE2EViewer, setShowE2EViewer] = useState(false);
  const [inviteForm, setInviteForm] = useState({ first_name: "", last_name: "", email: "", company_name: "", phone: "", notes: "", cover_image: "", background_image: "", envelope_text: "", envelope_bg_color: "", envelope_text_color: "", demo_tabs: [], prefill: {} });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [intakeTemplate, setIntakeTemplate] = useState({ sections: [] });
  const [loadingTemplate, setLoadingTemplate] = useState(false);

  // Fetch intake template - first try portal's own sections, then fall back to category template
  const fetchIntakeTemplate = async (cat) => {
    setLoadingTemplate(true);
    try {
      // First, try to get sections from the current sub-category (portal's own sections)
      // Check for actual sections content, not just empty array
      if (subCategoryData?.sections && subCategoryData.sections.length > 0) {
        setIntakeTemplate({ sections: subCategoryData.sections, category: cat });
        setLoadingTemplate(false);
        return;
      }
      
      // Get preset_id from subcategory if available (fallback to category)
      const presetId = subCategoryData?.preset_id || cat;
      
      // Always fetch from template API as fallback
      const res = await fetch(`${API}/admin/intake-template/${presetId.toLowerCase()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.sections && data.sections.length > 0) {
          setIntakeTemplate(data);
        }
      }
    } catch (err) {
      console.error("Error fetching intake template:", err);
    } finally {
      setLoadingTemplate(false);
    }
  };

  // Fetch template when category changes or dialog opens
  useEffect(() => {
    if (showAddInvite && category) {
      fetchIntakeTemplate(category);
    }
  }, [showAddInvite, category, subCategoryData]);

  const searchExistingUsers = async (q) => {
    if (!q || q.length < 2) { setUserSearchResults([]); return; }
    setSearchingUsers(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/users/search?q=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setUserSearchResults(data.users || []); }
    } catch {}
    finally { setSearchingUsers(false); }
  };

  useEffect(() => {
    const t = setTimeout(() => { if (userSearchQuery) searchExistingUsers(userSearchQuery); }, 300);
    return () => clearTimeout(t);
  }, [userSearchQuery]);

  const handleCreateInvite = async () => {
    if (!inviteForm.first_name && !inviteForm.email) { toast.error("First name or email required"); return; }
    setSendingInvite(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // Build prefill object with ALL data - include everything from both inviteForm and inviteForm.prefill
      const prefill = { ...inviteForm.prefill };
      
      // Always set category/pilot_type from current context
      prefill.pilot_type = category.toLowerCase();
      prefill.category = category.toLowerCase();
      
      // Sync top-level fields to prefill
      if (inviteForm.city) prefill.city = inviteForm.city;
      if (inviteForm.instagram) prefill.instagram = inviteForm.instagram;
      if (inviteForm.website) prefill.website = inviteForm.website;
      if (inviteForm.phone) prefill.phone = inviteForm.phone;
      
      // Use separate first/last name fields
      if (inviteForm.first_name) prefill.first_name = inviteForm.first_name;
      if (inviteForm.last_name) prefill.last_name = inviteForm.last_name;
      if (inviteForm.company_name) prefill.business_name = inviteForm.company_name;
      if (inviteForm.email) prefill.email = inviteForm.email;
      
      // Build full name for backwards compatibility
      const fullName = `${inviteForm.first_name || ""} ${inviteForm.last_name || ""}`.trim();
      
      const payload = {
        ...inviteForm,
        name: fullName, // For backwards compatibility
        prefill,
        pilot_type: category.toLowerCase(), // Explicitly set pilot_type
        cover_image: inviteForm.cover_image || null,
        background_image: inviteForm.background_image || null,
        // Envelope customization per-invite
        envelope_text: inviteForm.envelope_text || null,
        envelope_bg_color: inviteForm.envelope_bg_color || null,
        envelope_text_color: inviteForm.envelope_text_color || null,
        demo_tabs: inviteForm.demo_tabs || [category.toLowerCase()]
      };
      
      const res = await fetch(`${API}/admin/portal-categories/pilots/sub-categories/${subCategoryId}/invite`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success("Invite created!");
        const mt = data.invite?.magic_token || data.magic_token;
        if (mt) { const link = `${window.location.origin}/portal/invite/${mt}`; navigator.clipboard.writeText(link).then(() => toast.success("Invite link copied to clipboard!")); }
        setShowAddInvite(false); setInviteForm({ first_name: "", last_name: "", email: "", company_name: "", phone: "", notes: "", cover_image: "", background_image: "", envelope_text: "", envelope_bg_color: "", envelope_text_color: "", demo_tabs: [], prefill: {} }); setUserSearchQuery(""); setUserSearchResults([]); onRefresh?.();
      } else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Failed to create invite"); }
    } catch { toast.error("Error creating invite"); }
    finally { setSendingInvite(false); }
  };

  const selectExistingUser = (user) => {
    setInviteForm({
      first_name: user.first_name || (user.name ? user.name.split(" ")[0] : ""),
      last_name: user.last_name || (user.name ? user.name.split(" ").slice(1).join(" ") : ""),
      email: user.email || "",
      phone: user.phone || "",
      company_name: user.company_name || user.business_name || "",
      city: user.city || "",
      instagram: user.instagram_handle || user.instagram || "",
      website: user.website || "",
      notes: inviteForm.notes || "",
      cover_image: inviteForm.cover_image || "",
      background_image: inviteForm.background_image || "",
      demo_tabs: inviteForm.demo_tabs || [],
      prefill: inviteForm.prefill || {},
    });
    setUserSearchQuery("");
    setUserSearchResults([]);
    toast.success(`Filled from ${user.first_name || user.name || user.email}`);
  };

  const members = subCategoryData?.members || [];
  const pendingInvites = subCategoryData?.pending_invites || [];
  const allPeople = useMemo(() => [
    ...members.map(m => ({ ...m, type: "member", displayName: m.user_name || m.company_name || m.user_email || "Unknown", displayEmail: m.user_email })),
    ...pendingInvites.map(inv => ({ ...inv, type: "invite", displayName: inv.name || inv.email || "Unknown", displayEmail: inv.email })),
  ], [members, pendingInvites]);

  const filteredPeople = useMemo(() => allPeople.filter(person => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || person.displayName?.toLowerCase().includes(q) || person.displayEmail?.toLowerCase().includes(q) || person.company_name?.toLowerCase().includes(q);
    const matchesStatus = filterStatus === "all" || (filterStatus === "pending" && person.type === "invite") || (filterStatus === "active" && person.type === "member");
    return matchesSearch && matchesStatus;
  }), [allPeople, searchQuery, filterStatus]);

  const handleCopyInviteLink = (e, person) => { e.stopPropagation(); navigator.clipboard.writeText(`${window.location.origin}/portal/invite/${person.magic_token}`); toast.success("Invite link copied!"); };
  
  // Edit existing invite
  const handleEditInvite = (e, person) => {
    e.stopPropagation();
    if (person.type !== "invite") return;
    
    // Pre-populate the edit form with existing data
    setEditingInvite({
      invite_id: person.invite_id,
      first_name: person.first_name || person.name?.split(" ")[0] || "",
      last_name: person.last_name || person.name?.split(" ").slice(1).join(" ") || "",
      email: person.email || "",
      company_name: person.company_name || "",
      phone: person.phone || "",
      notes: person.notes || person.admin_notes || "",
      cover_image: person.cover_image || "",
      background_image: person.background_image || "",
      envelope_text: person.envelope_text || "",
      envelope_bg_color: person.envelope_bg_color || "",
      envelope_text_color: person.envelope_text_color || "",
      demo_tabs: person.demo_tabs || [category.toLowerCase()],
      prefill: person.prefill || {},
    });
    setShowEditInvite(true);
  };
  
  // Save edited invite
  const handleSaveEditedInvite = async () => {
    if (!editingInvite?.invite_id) return;
    setSavingEdit(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/portal-invites/${editingInvite.invite_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editingInvite)
      });
      if (res.ok) {
        toast.success("Invite updated!");
        setShowEditInvite(false);
        setEditingInvite(null);
        onRefresh?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update invite");
      }
    } catch {
      toast.error("Error updating invite");
    } finally {
      setSavingEdit(false);
    }
  };
  
  const handleDeletePerson = async (e, person) => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${person.displayName}?`)) return;
    try {
      const token = localStorage.getItem("session_token");
      const endpoint = person.type === "invite" ? `${API}/admin/portal-invites/${person.invite_id}` : `${API}/admin/portal-members/${person.member_id}`;
      const res = await fetch(endpoint, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Removed"); onRefresh?.(); } else toast.error("Failed to remove");
    } catch { toast.error("Error removing"); }
  };
  const getStatusBadge = (person) => {
    if (person.type === "invite") return <Badge variant="secondary" className="bg-amber-100 text-amber-700">Pending</Badge>;
    if (person.intake_completed) return <Badge className="bg-green-500">Intake Complete</Badge>;
    if (person.nda_signed) return <Badge className="bg-purple-500">NDA Signed</Badge>;
    return <Badge className="bg-blue-500">Accepted</Badge>;
  };

  return (
    <div className="p-6 space-y-4" data-testid="people-tab">
      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, email, or company..." className="pl-10" data-testid="people-search" />
        </div>
        <div className="flex gap-2">
          {[{ key: "all", label: `All (${allPeople.length})` }, { key: "active", label: `Active (${members.length})` }, { key: "pending", label: `Pending (${pendingInvites.length})` }].map(f => (
            <Button key={f.key} variant={filterStatus === f.key ? "default" : "outline"} size="sm" onClick={() => setFilterStatus(f.key)}>{f.label}</Button>
          ))}
        </div>
        <Button variant="outline" onClick={() => setShowAllIntakes(true)} data-testid="view-all-intakes-btn"><Eye className="w-4 h-4 mr-2" />Intakes</Button>
        <Button variant="outline" onClick={() => setShowE2EViewer(true)} data-testid="e2e-viewer-btn"><Settings className="w-4 h-4 mr-2" />E2E Viewer</Button>
        <Button className={categoryColors.bg} onClick={() => setShowAddInvite(true)} data-testid="add-participant-btn"><UserPlus className="w-4 h-4 mr-2" />Add</Button>
      </div>

      {/* People List */}
      <div className="space-y-2">
        {filteredPeople.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><Users className="w-12 h-12 mx-auto mb-4 text-gray-300" /><p className="text-gray-500">{searchQuery || filterStatus !== "all" ? "No participants match your filters" : "No participants yet. Create an invite to get started."}</p></CardContent></Card>
        ) : filteredPeople.map(person => (
          <Card key={person.invite_id || person.member_id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelectedPerson(person)} data-testid={`person-card-${person.invite_id || person.member_id}`}>
            <CardContent className="py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-full ${person.type === "invite" ? "bg-amber-500" : categoryColors.bg} text-white flex items-center justify-center shrink-0 font-medium`}>{person.displayName.charAt(0).toUpperCase()}</div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2"><p className="font-medium truncate">{person.displayName}</p>{getStatusBadge(person)}</div>
                    <p className="text-sm text-gray-500 truncate">{person.displayEmail}</p>
                    {person.company_name && <p className="text-xs text-gray-400 truncate flex items-center gap-1"><Building2 className="w-3 h-3" />{person.company_name}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  {person.type === "invite" && <Button variant="ghost" size="sm" onClick={e => handleCopyInviteLink(e, person)} title="Copy invite link"><Copy className="w-4 h-4" /></Button>}
                  {person.type === "invite" && <Button variant="ghost" size="sm" onClick={e => handleEditInvite(e, person)} title="Edit invite" data-testid={`edit-invite-${person.invite_id}`}><Edit2 className="w-4 h-4" /></Button>}
                  <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setSelectedPerson(person); }} title="View profile"><Eye className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={e => handleDeletePerson(e, person)} className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <PersonDetailModal person={selectedPerson} isOpen={!!selectedPerson} onClose={() => setSelectedPerson(null)} categoryColors={categoryColors} onRefresh={onRefresh} category={category} />

      {/* Add Invite Dialog */}
      <Dialog open={showAddInvite} onOpenChange={setShowAddInvite}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Invite to {category} Pilot</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* Search existing users */}
            <div>
              <Label>Search Existing People</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={userSearchQuery} onChange={e => { setUserSearchQuery(e.target.value); }} placeholder="Search by name or email..." className="pl-10" data-testid="user-search-input" />
              </div>
              {userSearchResults.length > 0 && (
                <div className="mt-1 border rounded-lg max-h-32 overflow-y-auto bg-white shadow-sm">
                  {userSearchResults.map((u, i) => (
                    <button key={u.user_id || i} onClick={() => selectExistingUser(u)} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm flex items-center gap-2 border-b last:border-0">
                      <User className="w-4 h-4 text-gray-400 shrink-0" />
                      <div><p className="font-medium">{u.first_name || u.name || "Unknown"} {u.last_name || ""}</p><p className="text-xs text-gray-500">{u.email}</p></div>
                    </button>
                  ))}
                </div>
              )}
              {searchingUsers && <p className="text-xs text-gray-400 mt-1">Searching...</p>}
            </div>
            <div className="border-t pt-3">
              <p className="text-xs text-gray-500 mb-3">Or fill in manually:</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>First Name *</Label><Input value={inviteForm.first_name || ""} onChange={e => setInviteForm(p => ({ ...p, first_name: e.target.value }))} placeholder="Jane" data-testid="invite-first-name" /></div>
                  <div><Label>Last Name *</Label><Input value={inviteForm.last_name || ""} onChange={e => setInviteForm(p => ({ ...p, last_name: e.target.value }))} placeholder="Doe" data-testid="invite-last-name" /></div>
                </div>
                <div><Label>Email *</Label><Input type="email" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} placeholder="jane@example.com" data-testid="invite-email" /></div>
                <div><Label>Company / Business Name</Label><Input value={inviteForm.company_name} onChange={e => setInviteForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Beauty Studio" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Phone</Label><Input value={inviteForm.phone || ""} onChange={e => setInviteForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 (555) 000-0000" /></div>
                  <div><Label>City</Label><LocationInput value={inviteForm.city || ""} onChange={(val) => setInviteForm(p => ({ ...p, city: val }))} placeholder="Search city..." /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Instagram</Label><Input value={inviteForm.instagram || ""} onChange={e => setInviteForm(p => ({ ...p, instagram: e.target.value }))} placeholder="@handle" /></div>
                  <div><Label>Website</Label><Input value={inviteForm.website || ""} onChange={e => setInviteForm(p => ({ ...p, website: e.target.value }))} placeholder="https://..." /></div>
                </div>
                <div><Label>Admin Notes</Label><Textarea value={inviteForm.notes || ""} onChange={e => setInviteForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Internal notes about this person" /></div>
              </div>
            </div>
            
            {/* Invite Customization Section */}
            <div className="border-t pt-3">
              <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2"><Palette className="w-4 h-4" />Envelope Customization</p>
              
              {/* Envelope Text */}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Envelope Text</Label>
                  <Input 
                    value={inviteForm.envelope_text || ""} 
                    onChange={e => setInviteForm(p => ({ ...p, envelope_text: e.target.value }))} 
                    placeholder="YOU'RE INVITED" 
                    data-testid="invite-envelope-text"
                  />
                  <p className="text-xs text-gray-400 mt-1">Defaults to "YOU'RE INVITED" if empty</p>
                </div>

                {/* Cover Image Upload (Logo for envelope) */}
                <div>
                  <Label className="text-xs">Envelope Logo / Cover Image</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {inviteForm.cover_image && (
                      <div className="relative">
                        <img src={inviteForm.cover_image} alt="Cover" className="w-12 h-12 rounded-full object-cover border" />
                        <button onClick={() => setInviteForm(p => ({ ...p, cover_image: "" }))} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">×</button>
                      </div>
                    )}
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append("file", file);
                        try {
                          const res = await fetch(`${API}/upload/cloudinary`, { method: "POST", body: formData });
                          if (res.ok) {
                            const data = await res.json();
                            setInviteForm(p => ({ ...p, cover_image: data.url }));
                            toast.success("Logo uploaded");
                          }
                        } catch { toast.error("Upload failed"); }
                      }}
                      className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                      data-testid="invite-cover-upload"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Shows in center of envelope</p>
                </div>

                {/* Background Image */}
                <div>
                  <Label className="text-xs">Envelope Background Image</Label>
                  <div className="flex items-center gap-2 mt-1">
                    {inviteForm.background_image && (
                      <div className="relative">
                        <img src={inviteForm.background_image} alt="Background" className="w-16 h-10 rounded object-cover border" />
                        <button onClick={() => setInviteForm(p => ({ ...p, background_image: "" }))} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">×</button>
                      </div>
                    )}
                    <input 
                      type="file" 
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append("file", file);
                        try {
                          const res = await fetch(`${API}/upload/cloudinary`, { method: "POST", body: formData });
                          if (res.ok) {
                            const data = await res.json();
                            setInviteForm(p => ({ ...p, background_image: data.url }));
                            toast.success("Background uploaded");
                          }
                        } catch { toast.error("Upload failed"); }
                      }}
                      className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                      data-testid="invite-bg-upload"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Uses chrome background if empty</p>
                </div>

                {/* Color Pickers */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Background Color</Label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="color"
                        value={inviteForm.envelope_bg_color || "#1a1a2e"}
                        onChange={(e) => setInviteForm(p => ({ ...p, envelope_bg_color: e.target.value }))}
                        className="w-8 h-8 rounded cursor-pointer border"
                      />
                      <Input
                        value={inviteForm.envelope_bg_color || ""}
                        onChange={(e) => setInviteForm(p => ({ ...p, envelope_bg_color: e.target.value }))}
                        placeholder="#1a1a2e"
                        className="text-xs h-8"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Text Color</Label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="color"
                        value={inviteForm.envelope_text_color || "#ffffff"}
                        onChange={(e) => setInviteForm(p => ({ ...p, envelope_text_color: e.target.value }))}
                        className="w-8 h-8 rounded cursor-pointer border"
                      />
                      <Input
                        value={inviteForm.envelope_text_color || ""}
                        onChange={(e) => setInviteForm(p => ({ ...p, envelope_text_color: e.target.value }))}
                        placeholder="#ffffff"
                        className="text-xs h-8"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Demo Tabs Selector */}
              <div className="mt-4">
                <Label className="text-xs">Demo Content Tabs</Label>
                <p className="text-xs text-gray-400 mb-2">Select which demo pages to show on the invite</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "makeup", label: "Makeup", icon: Palette },
                    { key: "hair", label: "Hair", icon: Scissors },
                    { key: "nail", label: "Nail", icon: Sparkles },
                    { key: "lash", label: "Lash", icon: Eye },
                    { key: "event", label: "Events", icon: Calendar },
                    { key: "wellness", label: "Wellness", icon: Heart },
                    { key: "instructor", label: "Instructor", icon: GraduationCap },
                    { key: "culinary", label: "Culinary", icon: ChefHat }
                  ].map(demo => {
                    const isSelected = (inviteForm.demo_tabs || [category.toLowerCase()]).includes(demo.key);
                    const Icon = demo.icon;
                    return (
                      <button
                        key={demo.key}
                        type="button"
                        onClick={() => {
                          const current = inviteForm.demo_tabs || [category.toLowerCase()];
                          const updated = isSelected 
                            ? current.filter(d => d !== demo.key)
                            : [...current, demo.key];
                          setInviteForm(p => ({ ...p, demo_tabs: updated.length > 0 ? updated : [category.toLowerCase()] }));
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                          isSelected 
                            ? `${categoryColors.bg} text-white` 
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                        data-testid={`demo-toggle-${demo.key}`}
                      >
                        <Icon className="w-3 h-3" />
                        {demo.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            
            {/* ALL Prefill Fields - Dynamic from Intake Template */}
            <div className="border-t pt-3">
              <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2"><ClipboardList className="w-4 h-4" />Pre-fill Intake Data (Optional)</p>
              <p className="text-xs text-gray-400 mb-3">Pre-fill any info you already know. The provider can edit these.</p>
              
              {loadingTemplate ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="w-4 h-4 animate-spin text-gray-400 mr-2" />
                  <span className="text-xs text-gray-500">Loading intake fields...</span>
                </div>
              ) : intakeTemplate.sections && intakeTemplate.sections.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                  {intakeTemplate.sections.map((section) => {
                    // COMPREHENSIVE EXCLUSION: Fields already collected in the main invite form
                    // These are: first_name, last_name, email, business_name, phone, city/location, instagram, website, tiktok, facebook
                    const excludedIds = [
                      'q_first_name', 'q_last_name', 'q_email', 
                      'q_business_name', 'q_phone', 'q_location', 'q_city',
                      'q_instagram', 'q_website', 'q_tiktok', 'q_facebook',
                      'q_name', 'q_full_name'
                    ];
                    const excludedLabels = [
                      'first name', 'last name', 'email', 
                      'business name', 'company', 'phone', 'telephone',
                      'location', 'city', 'instagram', 'website', 'tiktok', 'facebook',
                      'full name', 'your name'
                    ];
                    const excludedKeys = [
                      'first_name', 'last_name', 'email', 
                      'business_name', 'company_name', 'phone',
                      'location', 'city', 'instagram', 'website', 'tiktok', 'facebook',
                      'name', 'full_name'
                    ];
                    
                    // Filter out already-collected fields
                    const filteredQuestions = (section.questions || []).filter(q => {
                      // Check ID
                      if (excludedIds.includes(q.id)) return false;
                      // Check by field key/name
                      if (excludedKeys.includes(q.key || q.id?.replace('q_', ''))) return false;
                      // Check label (case-insensitive)
                      const labelLower = (q.label || '').toLowerCase();
                      if (excludedLabels.some(excl => labelLower.includes(excl))) return false;
                      // Also check placeholder
                      const placeholderLower = (q.placeholder || '').toLowerCase();
                      if (excludedLabels.some(excl => placeholderLower.includes(excl))) return false;
                      return true;
                    });
                    
                    if (filteredQuestions.length === 0) return null;
                    
                    return (
                      <div key={section.id} className="space-y-2 border-l-2 pl-3" style={{ borderColor: categoryColors.border || '#e5e7eb' }}>
                        <p className="text-xs font-medium text-gray-600">
                          {section.title}
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {filteredQuestions.map((question) => {
                            const fieldId = question.id;
                            const currentValue = inviteForm.prefill?.[fieldId];
                            
                            // Text, email, phone inputs
                            if (['text', 'email', 'phone'].includes(question.type)) {
                              return (
                                <div key={fieldId}>
                                  <Label className="text-xs">{question.label}</Label>
                                  <Input
                                    value={currentValue || ""}
                                    onChange={e => setInviteForm(p => ({ ...p, prefill: { ...p.prefill, [fieldId]: e.target.value } }))}
                                    placeholder={question.placeholder || ""}
                                    className="h-8 text-xs"
                                  />
                                </div>
                              );
                            }
                            
                            // Textarea
                            if (question.type === 'textarea') {
                              return (
                                <div key={fieldId} className="col-span-2">
                                  <Label className="text-xs">{question.label}</Label>
                                  <Textarea
                                    value={currentValue || ""}
                                    onChange={e => setInviteForm(p => ({ ...p, prefill: { ...p.prefill, [fieldId]: e.target.value } }))}
                                    placeholder={question.placeholder || ""}
                                    rows={2}
                                    className="text-xs"
                                  />
                                </div>
                              );
                            }
                            
                            // Multiselect - show as clickable tags
                            if (question.type === 'multiselect') {
                              return (
                                <div key={fieldId} className="col-span-2">
                                  <Label className="text-xs">{question.label}</Label>
                                  <div className="flex flex-wrap gap-1.5 mt-1">
                                    {(question.options || []).map((opt, idx) => {
                                      const optValue = typeof opt === 'object' ? opt.value : opt;
                                      const optLabel = typeof opt === 'object' ? opt.label : opt;
                                      const selected = Array.isArray(currentValue) ? currentValue.includes(optValue) : false;
                                      return (
                                        <button
                                          key={idx}
                                          type="button"
                                          onClick={() => {
                                            const current = Array.isArray(currentValue) ? currentValue : [];
                                            const newVal = selected
                                              ? current.filter(v => v !== optValue)
                                              : [...current, optValue];
                                            setInviteForm(p => ({ ...p, prefill: { ...p.prefill, [fieldId]: newVal } }));
                                          }}
                                          className={`px-2 py-1 text-xs rounded-full transition-colors ${
                                            selected
                                              ? `${categoryColors.bg} text-white`
                                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                          }`}
                                        >
                                          {optLabel}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            }
                            
                            // Yes/No toggle
                            if (question.type === 'yesno') {
                              return (
                                <div key={fieldId} className="flex items-center justify-between col-span-2">
                                  <Label className="text-xs">{question.label}</Label>
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => setInviteForm(p => ({ ...p, prefill: { ...p.prefill, [fieldId]: true } }))}
                                      className={`px-3 py-1 text-xs rounded ${currentValue === true ? `${categoryColors.bg} text-white` : 'bg-gray-100'}`}
                                    >Yes</button>
                                    <button
                                      type="button"
                                      onClick={() => setInviteForm(p => ({ ...p, prefill: { ...p.prefill, [fieldId]: false } }))}
                                      className={`px-3 py-1 text-xs rounded ${currentValue === false ? 'bg-red-500 text-white' : 'bg-gray-100'}`}
                                    >No</button>
                                  </div>
                                </div>
                              );
                            }
                            
                            // Toggle
                            if (question.type === 'toggle') {
                              return (
                                <div key={fieldId} className="flex items-center justify-between col-span-2">
                                  <div>
                                    <Label className="text-xs">{question.label}</Label>
                                    {question.description && <p className="text-xs text-gray-400">{question.description}</p>}
                                  </div>
                                  <Switch
                                    checked={currentValue || false}
                                    onCheckedChange={(checked) => setInviteForm(p => ({ ...p, prefill: { ...p.prefill, [fieldId]: checked } }))}
                                  />
                                </div>
                              );
                            }
                            
                            // File upload - show as file input
                            if (question.type === 'file') {
                              return (
                                <div key={fieldId} className="col-span-2">
                                  <Label className="text-xs">{question.label}</Label>
                                  <input 
                                    type="file" 
                                    accept="image/*"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      const formData = new FormData();
                                      formData.append("file", file);
                                      try {
                                        const res = await fetch(`${API}/upload/cloudinary`, { method: "POST", body: formData });
                                        if (res.ok) {
                                          const data = await res.json();
                                          setInviteForm(p => ({ ...p, prefill: { ...p.prefill, [fieldId]: data.url } }));
                                          toast.success("Uploaded");
                                        }
                                      } catch { toast.error("Upload failed"); }
                                    }}
                                    className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100"
                                  />
                                  {currentValue && <img src={currentValue} alt="Preview" className="w-12 h-12 mt-1 rounded object-cover" />}
                                </div>
                              );
                            }
                            
                            return null;
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400 py-2">No additional intake questions for this category.</p>
              )}
            </div>
          </div>
          <DialogFooter className="border-t pt-3">
            <Button variant="outline" onClick={() => setShowAddInvite(false)}>Cancel</Button>
            <Button onClick={handleCreateInvite} disabled={sendingInvite} className={categoryColors.bg} data-testid="send-invite-btn">
              {sendingInvite ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Create Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Intakes Dialog */}
      <Dialog open={showAllIntakes} onOpenChange={setShowAllIntakes}>
        <DialogContent className="max-w-4xl h-[90vh] overflow-hidden flex flex-col p-0">
          <div className="px-6 pt-5 pb-3 border-b">
            <h2 className="text-lg font-semibold flex items-center gap-2"><Eye className="w-5 h-5" />All Intake Responses: {category}</h2>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-6">
              {members.filter(m => m.intake_responses && Object.keys(m.intake_responses).length > 0).length === 0 ? (
                <div className="text-center py-12 text-gray-500"><ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" /><p>No intake responses yet</p></div>
              ) : members.filter(m => m.intake_responses && Object.keys(m.intake_responses).length > 0).map(member => {
                const grouped = groupIntakeData(member.intake_responses);
                return (
                  <Card key={member.member_id} className="border">
                    <CardHeader className="pb-2 pt-4 px-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full ${categoryColors.bg} text-white flex items-center justify-center text-sm font-medium`}>{(member.user_name || "U").charAt(0).toUpperCase()}</div>
                          <div><p className="font-medium text-sm">{member.user_name || "Unknown"}</p><p className="text-xs text-gray-500">{member.user_email}</p></div>
                        </div>
                        <Badge className={member.intake_completed ? "bg-green-500" : "bg-amber-500"}>{member.intake_completed ? "Complete" : "Partial"}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="px-4 pb-4 space-y-3">
                      {grouped.map(section => {
                        const SIcon = section.icon;
                        return (
                          <div key={section.title}>
                            <p className="text-xs font-semibold text-gray-500 flex items-center gap-1 mb-1.5"><SIcon className="w-3 h-3" />{section.title}</p>
                            <div className="bg-gray-50 rounded-lg p-3 space-y-2.5">
                              {section.fields.map(field => (
                                <div key={field.key}><p className="text-gray-500 text-xs">{field.label}</p><FieldValue field={field} /></div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin E2E Page Viewer */}
      <AdminE2EViewer
        isOpen={showE2EViewer}
        onClose={() => setShowE2EViewer(false)}
        people={allPeople}
        category={category}
        categoryColors={categoryColors}
        subCategoryId={subCategoryId}
        onRefresh={onRefresh}
      />

      {/* Edit Invite Dialog */}
      <Dialog open={showEditInvite} onOpenChange={(open) => { setShowEditInvite(open); if (!open) setEditingInvite(null); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle>Edit Invite</DialogTitle></DialogHeader>
          {editingInvite && (
            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              {/* Basic Info */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>First Name</Label><Input value={editingInvite.first_name || ""} onChange={e => setEditingInvite(p => ({ ...p, first_name: e.target.value }))} placeholder="Jane" data-testid="edit-first-name" /></div>
                  <div><Label>Last Name</Label><Input value={editingInvite.last_name || ""} onChange={e => setEditingInvite(p => ({ ...p, last_name: e.target.value }))} placeholder="Doe" data-testid="edit-last-name" /></div>
                </div>
                <div><Label>Email</Label><Input type="email" value={editingInvite.email || ""} onChange={e => setEditingInvite(p => ({ ...p, email: e.target.value }))} placeholder="jane@example.com" data-testid="edit-email" /></div>
                <div><Label>Company / Business Name</Label><Input value={editingInvite.company_name || ""} onChange={e => setEditingInvite(p => ({ ...p, company_name: e.target.value }))} placeholder="Beauty Studio" /></div>
                <div><Label>Phone</Label><Input value={editingInvite.phone || ""} onChange={e => setEditingInvite(p => ({ ...p, phone: e.target.value }))} placeholder="+1 (555) 000-0000" /></div>
                <div><Label>Admin Notes</Label><Textarea value={editingInvite.notes || ""} onChange={e => setEditingInvite(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Internal notes" /></div>
              </div>
              
              {/* Envelope Customization Section */}
              <div className="border-t pt-3">
                <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2"><Palette className="w-4 h-4" />Envelope Customization</p>
                
                <div className="space-y-3">
                  {/* Envelope Text */}
                  <div>
                    <Label className="text-xs">Envelope Text</Label>
                    <Input 
                      value={editingInvite.envelope_text || ""} 
                      onChange={e => setEditingInvite(p => ({ ...p, envelope_text: e.target.value }))} 
                      placeholder="YOU'RE INVITED" 
                      data-testid="edit-envelope-text"
                    />
                    <p className="text-xs text-gray-400 mt-1">Defaults to "YOU'RE INVITED" if empty</p>
                  </div>

                  {/* Cover Image Upload (Logo for envelope) */}
                  <div>
                    <Label className="text-xs">Envelope Logo / Cover Image</Label>
                    <div className="flex items-center gap-2 mt-1">
                      {editingInvite.cover_image && (
                        <div className="relative">
                          <img src={editingInvite.cover_image} alt="Cover" className="w-12 h-12 rounded-full object-cover border" />
                          <button onClick={() => setEditingInvite(p => ({ ...p, cover_image: "" }))} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">×</button>
                        </div>
                      )}
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append("file", file);
                          try {
                            const res = await fetch(`${API}/upload/cloudinary`, { method: "POST", body: formData });
                            if (res.ok) {
                              const data = await res.json();
                              setEditingInvite(p => ({ ...p, cover_image: data.url }));
                              toast.success("Logo uploaded");
                            }
                          } catch { toast.error("Upload failed"); }
                        }}
                        className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                      />
                    </div>
                  </div>

                  {/* Background Image */}
                  <div>
                    <Label className="text-xs">Envelope Background Image</Label>
                    <div className="flex items-center gap-2 mt-1">
                      {editingInvite.background_image && (
                        <div className="relative">
                          <img src={editingInvite.background_image} alt="Background" className="w-16 h-10 rounded object-cover border" />
                          <button onClick={() => setEditingInvite(p => ({ ...p, background_image: "" }))} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-xs flex items-center justify-center">×</button>
                        </div>
                      )}
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append("file", file);
                          try {
                            const res = await fetch(`${API}/upload/cloudinary`, { method: "POST", body: formData });
                            if (res.ok) {
                              const data = await res.json();
                              setEditingInvite(p => ({ ...p, background_image: data.url }));
                              toast.success("Background uploaded");
                            }
                          } catch { toast.error("Upload failed"); }
                        }}
                        className="text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                      />
                    </div>
                  </div>

                  {/* Color Pickers */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Background Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={editingInvite.envelope_bg_color || "#1a1a2e"}
                          onChange={(e) => setEditingInvite(p => ({ ...p, envelope_bg_color: e.target.value }))}
                          className="w-8 h-8 rounded cursor-pointer border"
                        />
                        <Input
                          value={editingInvite.envelope_bg_color || ""}
                          onChange={(e) => setEditingInvite(p => ({ ...p, envelope_bg_color: e.target.value }))}
                          placeholder="#1a1a2e"
                          className="text-xs h-8"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Text Color</Label>
                      <div className="flex gap-2 mt-1">
                        <input
                          type="color"
                          value={editingInvite.envelope_text_color || "#ffffff"}
                          onChange={(e) => setEditingInvite(p => ({ ...p, envelope_text_color: e.target.value }))}
                          className="w-8 h-8 rounded cursor-pointer border"
                        />
                        <Input
                          value={editingInvite.envelope_text_color || ""}
                          onChange={(e) => setEditingInvite(p => ({ ...p, envelope_text_color: e.target.value }))}
                          placeholder="#ffffff"
                          className="text-xs h-8"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Demo Tabs Selector */}
                <div className="mt-4">
                  <Label className="text-xs">Demo Content Tabs</Label>
                  <p className="text-xs text-gray-400 mb-2">Select which demo pages to show on the invite</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "makeup", label: "Makeup", icon: Palette },
                      { key: "hair", label: "Hair", icon: Scissors },
                      { key: "nail", label: "Nail", icon: Sparkles },
                      { key: "lash", label: "Lash", icon: Eye },
                      { key: "event", label: "Events", icon: Calendar },
                      { key: "wellness", label: "Wellness", icon: Heart },
                      { key: "instructor", label: "Instructor", icon: GraduationCap },
                      { key: "culinary", label: "Culinary", icon: ChefHat }
                    ].map(demo => {
                      const isSelected = (editingInvite.demo_tabs || [category.toLowerCase()]).includes(demo.key);
                      const Icon = demo.icon;
                      return (
                        <button
                          key={demo.key}
                          type="button"
                          onClick={() => {
                            const current = editingInvite.demo_tabs || [category.toLowerCase()];
                            const updated = isSelected 
                              ? current.filter(d => d !== demo.key)
                              : [...current, demo.key];
                            setEditingInvite(p => ({ ...p, demo_tabs: updated.length > 0 ? updated : [category.toLowerCase()] }));
                          }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                            isSelected 
                              ? `${categoryColors.bg} text-white` 
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          <Icon className="w-3 h-3" />
                          {demo.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Per-invite Overrides Section */}
                <div className="mt-4 border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Per-invite Overrides
                  </p>
                  <p className="text-xs text-gray-400 mb-3">Customize this invite's experience</p>
                  
                  {/* Enabled Defaults */}
                  <div className="mb-3">
                    <Label className="text-xs">Enabled Default Categories</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {["dietary_options", "accessibility_options", "vibe_options", "hair_options", "style_options", "travel_options"].map(def => {
                        const overrides = editingInvite.overrides || {};
                        const enabledDefaults = overrides.enabled_defaults || [];
                        const isEnabled = enabledDefaults.includes(def);
                        return (
                          <button
                            key={def}
                            type="button"
                            onClick={() => {
                              const current = enabledDefaults;
                              const updated = isEnabled ? current.filter(d => d !== def) : [...current, def];
                              setEditingInvite(p => ({ 
                                ...p, 
                                overrides: { ...(p.overrides || {}), enabled_defaults: updated }
                              }));
                            }}
                            className={`px-2 py-1 text-[10px] font-medium rounded-full transition-colors ${
                              isEnabled ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {def.replace("_options", "").replace("_", " ")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Feature Flags */}
                  <div className="space-y-2">
                    <Label className="text-xs">Feature Access</Label>
                    {[
                      { key: "can_create_events", label: "Can create events" },
                      { key: "can_invite_others", label: "Can invite others" },
                      { key: "can_see_analytics", label: "Can see analytics" }
                    ].map(feat => {
                      const overrides = editingInvite.overrides || {};
                      const features = overrides.features || {};
                      const isEnabled = features[feat.key] || false;
                      return (
                        <label key={feat.key} className="flex items-center gap-2 text-xs">
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={(e) => {
                              setEditingInvite(p => ({
                                ...p,
                                overrides: {
                                  ...(p.overrides || {}),
                                  features: { ...(p.overrides?.features || {}), [feat.key]: e.target.checked }
                                }
                              }));
                            }}
                            className="rounded"
                          />
                          {feat.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="border-t pt-4 mt-4">
            <Button variant="outline" onClick={() => { setShowEditInvite(false); setEditingInvite(null); }}>Cancel</Button>
            <Button className={categoryColors.bg} onClick={handleSaveEditedInvite} disabled={savingEdit} data-testid="save-edit-btn">
              {savingEdit ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Changes</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
